import { Theme } from '@branchjs/neo-components';
import type { Location } from 'history';
import React, { Component } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { Redirect, Route, BrowserRouter as Router, Switch, withRouter } from 'react-router-dom';
import ReferralCatcher from '../../common/support/ReferralCatcher';
import { CloudfrontService } from '../../main/services/CloudfrontService/CloudfrontService';
import ScreenCreateApp from '../screens/ScreenCreateApp';
import { ScreenEmailSent } from '../screens/ScreenEmailSent/ScreenEmailSent';
import { ScreenEmptyBranchAccountApps } from '../screens/ScreenEmptyBranchAccountApps/ScreenEmptyBranchAccountApps';
import ScreenLogin from '../screens/ScreenLogin/ScreenLogin';
import { ScreenResendVerificationEmail } from '../screens/ScreenResendVerificationEmail/ScreenResendVerificationEmail';
import { ScreenResetPassword } from '../screens/ScreenResetPassword/ScreenResetPassword';
import { ScreenResetPasswordLinkSent } from '../screens/ScreenResetPasswordLinkSent/ScreenResetPasswordLinkSent';
import ScreenSetPassword from '../screens/ScreenSetPassword/ScreenSetPassword';
/**
 * @description the below components have been rebuilt using React Functional components which leverage our @branchjs/neo-components library under the hood.
 */
import { ScreenSignup } from '../screens/ScreenSignup/ScreenSignup';
import ScreenSsoLogin from '../screens/ScreenSsoLogin';
import ScreenUnsubscribedAlertNotification from '../screens/ScreenUnsubscribedAlertNotification';
import { ScreenVerificationEmailSent } from '../screens/ScreenVerificationEmailSent/ScreenVerificationEmailSent';
import ScreenVerifyEmail from '../screens/ScreenVerifyEmail/ScreenVerifyEmail';

class App extends Component<RouteComponentProps> {
  /**
   * Determines whether the given SSO subdomain is not in the pre-production environment.
   *
   * @param {string} sso - The SSO subdomain to check.
   * @returns {boolean} - Returns `true` if the SSO subdomain is not `'dashboard-pre-prod'`, otherwise `false`.
   */
  isPreProd = (sso: string): boolean => {
    return sso !== 'dashboard-pre-prod';
  };

  /**
   * @method handleRedirect handles redirect for SSO users and users with no apps
   */
  handleRedirect = (): void => {
    const { pathname } = this.props.location as Location;
    // @ts-expect-error
    const currentAppError = window?.App?.context?.view?.error;
    // @ts-expect-error
    const sso = window?.App?.context?.ssoSubdomain;

    let redirectRoute = pathname;

    if (currentAppError === 'empty_app_list' && !sso) {
      redirectRoute = '/create';
    } else if (currentAppError === 'empty_app_list_branch_account' && !sso) {
      redirectRoute = '/no-branch-account-app';
    } else if (currentAppError === 'empty_app_list' && sso && this.isPreProd(sso)) {
      redirectRoute = '/sso/login/error';
    } else if (sso && this.isPreProd(sso) && !CloudfrontService.isCloudfrontUrl(window.location.href)) {
      redirectRoute = '/sso/login';
    } else if ((!sso || this.isPreProd(sso)) && pathname.indexOf('sso') > -1) {
      redirectRoute = '/login';
    }

    if (redirectRoute !== pathname) {
      this.props.history.push(redirectRoute);
    }
  };

  render() {
    this.handleRedirect();
    const acceptInviteUserEmail = localStorage.getItem('accept_invite_user_email');
    const passwordResetUserEmailAddress = localStorage.getItem('reset_password_user_email');
    const emailSentRouteIsEnabled = this.props.location.state?.emailSentRouteEnabled === true;

    return (
      <div className="loggedout">
        <Theme injectFirst>
          <Switch>
            <Route path="/" component={ScreenSignup} exact />
            <Route path="/login" component={ScreenLogin} exact />
            <Route path="/login/:reset_key" component={ScreenSetPassword} />
            <Route path="/login-accept" component={ScreenLogin} exact />
            <Route path="/login-accept/:accept_key" component={ScreenSetPassword} />
            <Route path="/verify" component={ScreenVerifyEmail} />
            {acceptInviteUserEmail && (
              <Route
                exact
                path="/accept-email-invite"
                render={() => <ScreenVerificationEmailSent userEmailAddress={acceptInviteUserEmail} />}
              />
            )}
            {acceptInviteUserEmail && (
              <Route
                exact
                path="/resend-verification-email"
                render={() => <ScreenResendVerificationEmail userEmailAddress={acceptInviteUserEmail} />}
              />
            )}
            {emailSentRouteIsEnabled && <Route exact path="/email-sent" component={ScreenEmailSent} />}
            <Route
              exact
              path="/reset-password"
              component={() => <ScreenResetPassword userEmailAddress={passwordResetUserEmailAddress} />}
            />
            {passwordResetUserEmailAddress && emailSentRouteIsEnabled && (
              <Route
                exact
                path="/reset-password-link-sent"
                component={() => <ScreenResetPasswordLinkSent userEmailAddress={passwordResetUserEmailAddress} />}
              />
            )}
            <Route path="/create" component={ScreenCreateApp} />
            <Route path="/sso/login" component={ScreenSsoLogin} exact />
            <Route path="/sso/login/:error" component={ScreenSsoLogin} />
            <Route path="/no-branch-account-app" component={ScreenEmptyBranchAccountApps} />
            <Route
              path="/alert-notifications/alert/:alertId/recipient/:emailAddress/unsubscribe/:token"
              component={ScreenUnsubscribedAlertNotification}
            />
            <Redirect from="*" to="/" />
          </Switch>
        </Theme>
      </div>
    );
  }
}

const AppWithRouter = withRouter(App);

const RouterLoggedout = () => {
  ReferralCatcher.storeExternalReferrer();

  return (
    <Router>
      <Route path="/" component={AppWithRouter} />
    </Router>
  );
};

export default RouterLoggedout;
